<template>
  <div v-if="hasAny" class="sai-row" :title="titleText">
    <!-- Wind Protection -->
    <div v-if="windState !== null" class="sai-slot" :class="windClass">
      <div v-if="windState === 'active'" class="sai-ping"></div>
      <span class="material-symbols-outlined sai-icon">air</span>
    </div>

    <!-- Rain Protection -->
    <div v-if="rainState !== null" class="sai-slot" :class="rainClass">
      <div v-if="rainState === 'active'" class="sai-ping"></div>
      <span class="material-symbols-outlined sai-icon">water_drop</span>
    </div>

    <!-- Sun Tracking -->
    <div v-if="sunState !== null" class="sai-slot" :class="sunClass">
      <span class="material-symbols-outlined sai-icon">wb_sunny</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { tt } from '../../utils/consoleText';

interface Props {
  /** null = automation not configured on this device */
  windState: 'active' | 'resuming' | 'idle' | null;
  rainState: 'active' | 'resuming' | 'idle' | null;
  sunState: 'active' | 'idle' | null;
}

const props = defineProps<Props>();

const hasAny = computed(() => props.windState !== null || props.rainState !== null || props.sunState !== null);

const windClass = computed(() => {
  if (props.windState === 'active') return 'sai-slot--wind-active';
  if (props.windState === 'resuming') return 'sai-slot--resuming';
  return 'sai-slot--idle';
});

const rainClass = computed(() => {
  if (props.rainState === 'active') return 'sai-slot--rain-active';
  if (props.rainState === 'resuming') return 'sai-slot--resuming';
  return 'sai-slot--idle';
});

const sunClass = computed(() => {
  if (props.sunState === 'active') return 'sai-slot--sun-active';
  return 'sai-slot--idle';
});

const titleText = computed(() => {
  const parts: string[] = [];
  if (props.windState === 'active')
    parts.push(tt('Wind protection active — shade retracted', 'Zaščita pred vetrom aktivna — senčilo dvignjeno'));
  else if (props.windState === 'resuming')
    parts.push(tt('Wind eased — waiting to resume', 'Veter se je umiril — čakanje na nadaljevanje'));
  else if (props.windState === 'idle')
    parts.push(tt('Wind protection standby', 'Zaščita pred vetrom v pripravljenosti'));

  if (props.rainState === 'active')
    parts.push(tt('Rain protection active — shade retracted', 'Zaščita pred dežjem aktivna — senčilo dvignjeno'));
  else if (props.rainState === 'resuming')
    parts.push(tt('Rain stopped — waiting to resume', 'Dež je prenehal — čakanje na nadaljevanje'));
  else if (props.rainState === 'idle')
    parts.push(tt('Rain protection standby', 'Zaščita pred dežjem v pripravljenosti'));

  if (props.sunState === 'active') parts.push(tt('Sun tracking active', 'Sončno vodenje aktivno'));
  else if (props.sunState === 'idle') parts.push(tt('Sun tracking standby', 'Sončno vodenje v pripravljenosti'));

  return parts.join(' · ');
});
</script>
